/**
 * Shared TodoCard for Todos view, Triage, and My Stuff.
 * Table-row layout (CSS grid) with aligned columns, overflow menu, and inline due-date editing.
 *
 * Ported from prototype: frontend/src/components/test-basecamp-todos.tsx
 * Bet: docs/product/bets/2026-03-25-basecamp-todos-table-report-widget.md
 */

import React, { useState, useEffect, useRef, isValidElement } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  MoreHorizontal, Loader2, type LucideIcon,
} from 'lucide-react';
import type { BasecampAssignment } from '@/types/basecamp';
import type { EntityContextMenuAction, EntityContextMenuEntry } from '@/components/features/entity-context-menu';
import { buildTodosRowContextMenuEntries } from './basecamp-todos-row-context-menu';
import { classifyAssigneeKind, formatAssigneesLabel, type BasecampPersonRef } from '@/lib/basecamp-assignee';
import { BASECAMP_DUE_BADGE_COMPACT_CLASS, BasecampDueBadge } from './components/basecamp-due-badge'
import { cn } from '@/lib/utils';
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome';

function normalizeDueForInput(due: string | null | undefined): string {
  if (due == null || due === '') return '';
  const trimmed = String(due).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  return '';
}

export type WorkspecStatus = 'none' | 'in_sync' | 'stale';

export interface TodoCardProps {
  todo: BasecampAssignment;
  /** `undefined` = workspec not loaded yet; object = loaded (use status `none` for no work spec) */
  workSpecStatus?: { status: string; filePath: string } | null;
  lastComment?: { text: string; createdAt: string; creatorName?: string; stale?: boolean } | null;
  onDueDateChange?: (todoId: string, newDate: string) => void;
  onOpenFlowNotes?: (todoId: string, event?: React.MouseEvent) => void;
  onOpenBasecamp?: (url: string) => void;
  onRefreshRow?: (todoId: string) => void;
  isRefreshing?: boolean;
  /** True while auto-enrich or row refresh is fetching spec/activity for this row */
  isEnriching?: boolean;
  isSelected?: boolean;
  onSelect?: (todoId: string, event?: React.MouseEvent) => void;
  showDueDateEdit?: boolean;
  onPreviewWorkSpec?: (filePath: string, title?: string) => void;
  /** Pin the list preview pane for this row (ADR-0020 Preview). */
  onPreview?: () => void;
  /** Open the collapsing list find (same as Cmd+F). */
  onSearch?: () => void;
  gridTemplate?: string;
  onContextMenu?: (e: React.MouseEvent) => void;
  rowTestId?: string;
  /** BC5 subtask row styling */
  isSubtaskRow?: boolean;
  indent?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function assigneeKindTitle(
  assignees: BasecampPersonRef[] | undefined,
  fallback?: { id?: string; name?: string; email?: string; client?: boolean } | null,
): string {
  const people: BasecampPersonRef[] = assignees?.length
    ? assignees
    : fallback?.name
      ? [{
          id: String(fallback.id ?? ''),
          name: fallback.name,
          email: fallback.email,
          client: fallback.client,
        }]
      : [];
  return people
    .filter((p) => p.name)
    .map((p) => {
      const kind = classifyAssigneeKind(p);
      if (kind === 'hop') return `${p.name} (Hop)`;
      if (kind === 'client') return `${p.name} (Client)`;
      return p.name;
    })
    .join(', ');
}

function formatLastActivity(createdAt: string): string {
  if (!createdAt) return '\u2014';
  const d = new Date(createdAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

// ─── Due date cell ───────────────────────────────────────────────────────────

function addDaysToDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function DueDateCell({ todo, onDueDateChange, showEdit }: {
  todo: BasecampAssignment;
  onDueDateChange?: (todoId: string, newDate: string) => void;
  showEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(() => normalizeDueForInput(todo.due_on));
  const [pos, setPos] = useState<{ x: number; y: number; up: boolean }>({ x: 0, y: 0, up: true });
  const panelRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editing) setEditValue(normalizeDueForInput(todo.due_on));
  }, [editing, todo.due_on]);

  useEffect(() => {
    if (!editing) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setEditing(false);
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditing(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [editing]);

  const isDirty = editValue !== normalizeDueForInput(todo.due_on);

  const handleSave = () => {
    const dateToSave = normalizeDueForInput(editValue);
    if (onDueDateChange && dateToSave) onDueDateChange(todo.id, dateToSave);
    setEditing(false);
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const up = rect.top > 160;
      setPos({
        x: Math.min(rect.left, window.innerWidth - 200),
        y: up ? rect.top : rect.bottom,
        up,
      });
    }
    setEditing(true);
  };

  const dueBadge = (
    <BasecampDueBadge
      dueOn={todo.due_on}
      isOverdue={todo._is_overdue}
      className={`cursor-pointer ${BASECAMP_DUE_BADGE_COMPACT_CLASS}`}
    />
  );

  if (!showEdit) return dueBadge;

  return (
    <div ref={anchorRef}>
      <button onClick={handleOpen} className="text-left w-full group/due" title="Click to edit due date">
        {dueBadge ?? <span className="text-xs text-muted-foreground/30 group-hover/due:text-muted-foreground transition-colors">Set date</span>}
      </button>
      {editing && (
        <div
          ref={panelRef}
          className="fixed z-50 bg-popover border rounded-lg shadow-lg p-2.5 w-[11rem] space-y-1.5 animate-in fade-in-0 zoom-in-95"
          style={{
            left: pos.x,
            ...(pos.up ? { bottom: window.innerHeight - pos.y + 4 } : { top: pos.y + 4 }),
          }}
          onClick={e => e.stopPropagation()}
        >
          <input
            type="date"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && isDirty) handleSave(); }}
            className="h-7 w-full px-1.5 rounded border border-input bg-background text-xs [color-scheme:light] dark:[color-scheme:dark]"
            autoFocus
          />
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                const base = editValue || normalizeDueForInput(todo.due_on);
                if (base) setEditValue(addDaysToDate(base, 7));
              }}
              disabled={!editValue && !todo.due_on}
              className="h-7 flex-1 px-2 rounded border border-border text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors whitespace-nowrap disabled:opacity-40"
            >
              +1 week
            </button>
            <button
              onClick={handleSave}
              disabled={!isDirty}
              className="h-7 flex-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-30 disabled:pointer-events-none"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Row actions dropdown ────────────────────────────────────────────────────

function isSeparatorEntry(entry: EntityContextMenuEntry): entry is { separator: true } {
  return 'separator' in entry && entry.separator === true;
}

function OverflowMenuIcon({ icon }: { icon?: EntityContextMenuAction['icon'] }) {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  if (typeof icon === 'function' || (typeof icon === 'object' && icon !== null && 'render' in icon)) {
    const Icon = icon as LucideIcon;
    return <Icon className="w-3.5 h-3.5 flex-none" />;
  }
  return null;
}

function RowActionsDropdown({ todo, onPreview, onSearch, onPreviewWorkSpec, onRefreshRow, workSpecStatus }: {
  todo: BasecampAssignment;
  onPreview?: () => void;
  onSearch?: () => void;
  onRefreshRow?: (todoId: string) => void;
  onPreviewWorkSpec?: (filePath: string, title?: string) => void;
  workSpecStatus?: { status: string; filePath: string } | null;
}) {
  const entries = buildTodosRowContextMenuEntries({
    todo,
    onPreview,
    onSearch,
    onRefresh: onRefreshRow ? () => onRefreshRow(todo.id) : undefined,
    workSpecStatus,
    onViewWorkSpec: workSpecStatus?.filePath
      ? () => onPreviewWorkSpec?.(workSpecStatus.filePath, todo.title)
      : undefined,
    close: () => {},
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground/30 hover:text-foreground group-hover:text-muted-foreground/60">
          <MoreHorizontal className="w-3.5 h-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
      >
        {entries.map((entry, index) => {
          if (isSeparatorEntry(entry)) {
            return <DropdownMenuSeparator key={`sep-${index}`} />;
          }
          if (!('onSelect' in entry)) return null;
          return (
            <DropdownMenuItem
              key={`${entry.label}-${index}`}
              disabled={entry.disabled}
              onClick={() => entry.onSelect()}
            >
              <OverflowMenuIcon icon={entry.icon} />
              <span className="flex-1">{entry.label}</span>
              {entry.hint ? (
                <span className="ml-4 text-xs tabular-nums text-muted-foreground">
                  {entry.hint}
                </span>
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Work spec badge ─────────────────────────────────────────────────────────

function WorkspecBadgeCell({ workSpecStatus, onPreviewWorkSpec, todoTitle, isEnriching }: {
  workSpecStatus?: { status: string; filePath: string } | null;
  onPreviewWorkSpec?: (filePath: string, title?: string) => void;
  todoTitle: string;
  isEnriching?: boolean;
}) {
  if (workSpecStatus === undefined) {
    if (isEnriching) {
      return <span className="text-xs text-muted-foreground" title="Loading work spec status">…</span>;
    }
    return <span className="text-xs text-muted-foreground" title="Work spec not loaded yet">—</span>;
  }
  const status = (workSpecStatus?.status ?? 'none') as WorkspecStatus;
  if (!status || status === 'none') return <span className="text-xs text-muted-foreground">No spec</span>;
  if (status === 'in_sync') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-500 hover:underline cursor-pointer"
            onClick={e => { e.stopPropagation(); workSpecStatus?.filePath && onPreviewWorkSpec?.(workSpecStatus.filePath, todoTitle); }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block flex-none" />In sync
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs"><p>Basecamp and work spec are synced.</p></TooltipContent>
      </Tooltip>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block flex-none" />Out of date
    </span>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function TodoCard({
  todo,
  workSpecStatus,
  lastComment,
  onDueDateChange,
  onOpenFlowNotes,
  onRefreshRow,
  isRefreshing = false,
  isEnriching = false,
  isSelected,
  onSelect,
  showDueDateEdit = false,
  onPreviewWorkSpec,
  onPreview,
  onSearch,
  gridTemplate,
  onContextMenu,
  rowTestId,
  isSubtaskRow = false,
  indent = false,
}: TodoCardProps) {
  const projectName = todo._project_name ?? todo.project?.name ?? '';
  const parentTitle = todo._parent_title ?? '';
  const isOverdue = todo._is_overdue ?? false;
  const listLabel = isSubtaskRow ? (parentTitle || 'Subtask') : parentTitle;

  if (gridTemplate) {
    const td = 'border-b border-border/50 py-1.5';
    return (
      <tr
        data-testid={rowTestId}
        className={cn(
          'group cursor-pointer transition-colors',
          'hover:bg-accent/40',
          isSelected && 'bg-primary/5 hover:bg-primary/10',
          isOverdue && !isSelected && 'bg-red-500/[0.03] hover:bg-red-500/[0.07]',
        )}
        onClick={(e) => onSelect?.(todo.id, e)}
        onDoubleClick={(e) => {
          e.preventDefault();
          onOpenFlowNotes?.(todo.id, e);
        }}
        onContextMenu={onContextMenu}
      >
        {/* Stripe */}
        <td className={td} style={{ backgroundColor: projectName ? stringToColor(projectName) : '#666', opacity: 0.7, padding: 0 }} />

        {/* Project */}
        <td className={cn(td, 'px-3 overflow-hidden')}>
          <span className="text-[11px] font-medium text-muted-foreground truncate block" title={projectName}>
            {projectName || '\u2014'}
          </span>
        </td>

        {/* Title */}
        <td className={cn(td, 'px-2 overflow-hidden', indent && 'pl-6')}>
          <div className="flex items-center gap-1.5 min-w-0">
            {isSubtaskRow ? (
              <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0 font-normal">
                Subtask
              </Badge>
            ) : null}
            <span className="text-sm font-medium truncate leading-snug" title={todo.title}>{todo.title}</span>
          </div>
        </td>

        {/* Todolist / parent */}
        <td className={cn(td, 'px-2 text-xs text-muted-foreground overflow-hidden')} title={listLabel}>
          <span className="truncate block">{listLabel || '\u2014'}</span>
        </td>

        {/* Assignee */}
        <td className={cn(td, 'px-2 text-xs text-foreground overflow-hidden')}>
          {(() => {
            const label = formatAssigneesLabel(todo.assignees, todo.assignee);
            const detail = assigneeKindTitle(todo.assignees, todo.assignee);
            return (
              <span className="truncate block" title={detail || undefined}>
                {label || '\u2014'}
              </span>
            );
          })()}
        </td>

        {/* Work spec */}
        <td className={cn(td, 'px-2')} onClick={e => e.stopPropagation()}>
          <WorkspecBadgeCell workSpecStatus={workSpecStatus} onPreviewWorkSpec={onPreviewWorkSpec} todoTitle={todo.title} isEnriching={isEnriching} />
        </td>

        {/* Activity */}
        <td className={cn(td, 'px-2 text-xs text-muted-foreground overflow-hidden')}>
          {lastComment ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help truncate block">
                  <span>{formatLastActivity(lastComment.createdAt)}</span>
                  {' '}&middot; &ldquo;{lastComment.text.slice(0, 28)}{lastComment.text.length > 28 ? '\u2026' : ''}&rdquo;
                  {lastComment.stale ? (
                    <span className="ml-1 text-[10px] text-muted-foreground whitespace-nowrap">(cache)</span>
                  ) : null}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {lastComment.creatorName && <p className="font-medium text-xs mb-0.5">{lastComment.creatorName}</p>}
                <p className="text-xs line-clamp-3">{lastComment.text}</p>
              </TooltipContent>
            </Tooltip>
          ) : <span className="text-muted-foreground">&mdash;</span>}
        </td>

        {/* Due */}
        <td className={cn(td, 'px-2')}>
          <DueDateCell todo={todo} onDueDateChange={onDueDateChange} showEdit={showDueDateEdit} />
        </td>

        {/* Actions */}
        <td className={cn(td, 'px-1 text-center')} onClick={e => e.stopPropagation()}>
          {isRefreshing
            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground/60 mx-auto" />
            : <RowActionsDropdown todo={todo} onPreview={onPreview} onSearch={onSearch} onRefreshRow={onRefreshRow} onPreviewWorkSpec={onPreviewWorkSpec} workSpecStatus={workSpecStatus} />
          }
        </td>
      </tr>
    );
  }

  // Fallback: flex card layout (backward compat, used when no gridTemplate is provided)
  return (
    <div
      className={cn(
        'group border-b border-border/50 last:border-0 transition-colors cursor-pointer',
        'hover:bg-accent/40',
        isSelected && 'bg-primary/5 hover:bg-primary/10',
        isOverdue && !isSelected && 'bg-red-500/[0.03] hover:bg-red-500/[0.07]'
      )}
      onClick={(e) => onSelect?.(todo.id, e)}
      onDoubleClick={(e) => {
        e.preventDefault();
        onOpenFlowNotes?.(todo.id, e);
      }}
      onContextMenu={onContextMenu}
    >
      <div className="flex items-start gap-2.5 px-3 py-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {projectName && (
              <span className="flex-none text-[11px] font-medium text-muted-foreground truncate max-w-[100px] xl:max-w-[140px]" title={projectName}>
                {projectName}
              </span>
            )}
            <span className="flex-1 min-w-0 text-sm font-medium truncate leading-snug" title={todo.title}>{todo.title}</span>
            <DueDateCell todo={todo} onDueDateChange={onDueDateChange} showEdit={false} />
            <div className="flex-none" onClick={e => e.stopPropagation()}>
              {isRefreshing
                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground/60" />
                : <RowActionsDropdown todo={todo} onPreview={onPreview} onSearch={onSearch} onRefreshRow={onRefreshRow} onPreviewWorkSpec={onPreviewWorkSpec} workSpecStatus={workSpecStatus} />
              }
            </div>
          </div>
          <div className="flex items-center gap-x-2.5 gap-y-0 mt-0.5 text-xs text-muted-foreground min-w-0">
            {parentTitle && <span className="flex-none truncate max-w-[120px] xl:max-w-[180px]" title={parentTitle}>{parentTitle} &rsaquo;</span>}
            <span className="flex-none" onClick={e => e.stopPropagation()}>
              <WorkspecBadgeCell workSpecStatus={workSpecStatus} onPreviewWorkSpec={onPreviewWorkSpec} todoTitle={todo.title} isEnriching={isEnriching} />
            </span>
            {lastComment && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate max-w-[200px] xl:max-w-[300px] cursor-help">
                    {formatLastActivity(lastComment.createdAt)} &middot; &ldquo;{lastComment.text.slice(0, 40)}{lastComment.text.length > 40 ? '\u2026' : ''}&rdquo;
                    {lastComment.stale ? (
                      <span className="ml-1 text-[10px] text-muted-foreground whitespace-nowrap">(cache)</span>
                    ) : null}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {lastComment.creatorName && <p className="font-medium text-xs mb-0.5">{lastComment.creatorName}</p>}
                  <p className="text-xs line-clamp-3">{lastComment.text}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {showDueDateEdit && (
              <span className="flex-none" onClick={e => e.stopPropagation()}>
                <DueDateCell todo={todo} onDueDateChange={onDueDateChange} showEdit={true} />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = hash % 360;
  return `hsl(${h < 0 ? h + 360 : h}, 55%, 45%)`;
}
